import { Show } from "@refinedev/antd";
import { useShow } from "@refinedev/core";
import { useNavigate, useParams } from "react-router";
import {
  Card,
  Descriptions,
  Button,
  Space,
  Empty,
  Tag,
  Spin,
  Table,
  Modal,
  Form,
  DatePicker,
  InputNumber,
  Input,
  AutoComplete,
  App,
} from "antd";
import {
  ArrowLeftOutlined,
  InfoCircleOutlined,
  HistoryOutlined,
  ExperimentOutlined,
  PictureOutlined,
  CloseCircleOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { ControlPointDetail, ControlPointStatus } from "../../types/control-point";
import dayjs from "dayjs";
import { PhotoGalleryModal } from "../../components/PhotoGalleryModal";
import { ControlPointLocationMap } from "./components/ControlPointLocationMap";
import { useState, useCallback, useRef, useEffect } from "react";
import { controlPointService } from "../../services/api";
import "./control-points.css";
import { usePageTitle } from "../../hooks";

const { TextArea } = Input;

/**
 * ControlPointShow Component
 * Displays detailed information about a control point including:
 * - General information
 * - Map with location
 * - Review history timeline
 * - Samples table
 * - Photos from each review
 */
export const ControlPointShow = () => {
  const { t } = useTranslation();
  // Ant 5: modal via useApp() para que participe del <App> context
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  const { query } = useShow<ControlPointDetail>({
    resource: "control-points",
    id,
  });

  const { data, isLoading, refetch } = query;
  const controlPoint = data?.data;
  
  // Set page title
  usePageTitle(controlPoint?.name || t("controlPoint.title", "Punto de Control"));
  
  const [photoGalleryVisible, setPhotoGalleryVisible] = useState(false);
  const [closeModalVisible, setCloseModalVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [farmOptions, setFarmOptions] = useState<{ value: string; label: string }[]>([]);
  const [searchingFarms, setSearchingFarms] = useState(false);
  const [form] = Form.useForm();
  const searchTimeoutRef = useRef<number | null>(null);
  
  // Sample modal states
  const [sampleModalVisible, setSampleModalVisible] = useState(false);
  const [sampleForm] = Form.useForm();
  const [editingSample, setEditingSample] = useState<any>(null);
  const [isSavingSample, setIsSavingSample] = useState(false);

  // Search farms dynamically with debounce
  const handleSearchFarms = useCallback((searchValue: string) => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Clear options if search is empty or too short
    if (!searchValue || searchValue.trim().length < 2) {
      setFarmOptions([]);
      return;
    }

    // Debounce the search
    searchTimeoutRef.current = setTimeout(async () => {
      await performFarmSearch(searchValue);
    }, 500); // Wait 500ms after user stops typing
  }, [controlPoint]);

  // Actual search function
  const performFarmSearch = async (searchValue: string) => {

    try {
      setSearchingFarms(true);
      const { apiClient } = await import("../../services/api/apiClient");
      
      // Build params with country filter if available
      const params: any = {
        search: searchValue.trim(),
        page_size: 20,
      };

      // Get the country from the control point's farm
      if (controlPoint?.farm) {
        try {
          const farmResponse = await apiClient.get(`/farms/${controlPoint.farm}/`);
          if (farmResponse.data?.country) {
            params.country = farmResponse.data.country;
          }
        } catch (error) {
          console.error('Error getting farm country:', error);
        }
      }
      
      const response = await apiClient.get('/farms/', { params });

      const farms = response.data.results || response.data;
      
      console.log('Farm search results:', {
        searchValue,
        params,
        totalResults: farms?.length,
        farms: farms?.slice(0, 5) // Log first 5 for debugging
      });
      
      // Only set options if we have results
      if (farms && farms.length > 0) {
        const options = farms.slice(0, 20).map((farm: any, index: number) => {
          const displayName = farm.display_name || `${farm.producer?.name || ''} - ${farm.name} - ${farm.town?.name || ''}`;
          return {
            value: displayName, // Use display_name as value so it shows in the input
            label: displayName,
            key: `${farm.id || index}`, // Use farm ID as unique key
          };
        });
        setFarmOptions(options);
      } else {
        setFarmOptions([]);
      }
    } catch (error) {
      console.error('Error searching farms:', error);
      setFarmOptions([]);
    } finally {
      setSearchingFarms(false);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleBack = () => {
    navigate("/control-points");
  };

  const handleViewPhotos = () => {
    setPhotoGalleryVisible(true);
  };

  const handleOpenCloseModal = () => {
    form.resetFields();
    setCloseModalVisible(true);
  };

  const handleCloseModalCancel = () => {
    setCloseModalVisible(false);
    form.resetFields();
  };

  const handleCloseControlPoint = async (values: any) => {
    if (!id) return;

    try {
      setIsClosing(true);
      
      await controlPointService.closeControlPoint(id, {
        closed_date: values.closed_date.format("YYYY-MM-DD"),
        comment_closed: values.comment_closed,
        converted_hectares: values.converted_hectares || 0,
        authorized_farm_reference: values.authorized_farm_reference,
      });

      message.success("Punto de control cerrado exitosamente");
      setCloseModalVisible(false);
      form.resetFields();
      refetch();
    } catch (error: any) {
      message.error(error.message || "Error al cerrar el punto de control");
    } finally {
      setIsClosing(false);
    }
  };

  // Sample handlers
  const handleCreateSample = () => {
    setEditingSample(null);
    sampleForm.resetFields();
    setSampleModalVisible(true);
  };

  const handleEditSample = (sample: any) => {
    setEditingSample(sample);
    sampleForm.setFieldsValue({
      code_sample: sample.code_sample,
      code_plot: sample.code_plot,
      position_waypoint: sample.position_waypoint,
    });
    setSampleModalVisible(true);
  };

  const handleDeleteSample = async (sampleId: string) => {
    modal.confirm({
      title: '¿Eliminar muestra?',
      content: '¿Está seguro de que desea eliminar esta muestra?',
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const { apiClient } = await import("../../services/api/apiClient");
          await apiClient.delete(`/samples/${sampleId}/`);
          message.success('Muestra eliminada correctamente');
          refetch();
        } catch (error: any) {
          message.error('Error al eliminar la muestra');
          console.error('Error deleting sample:', error);
        }
      },
    });
  };

  const handleSaveSample = async (values: any) => {
    setIsSavingSample(true);
    try {
      const { apiClient } = await import("../../services/api/apiClient");
      
      const sampleData = {
        code_sample: values.code_sample,
        code_plot: values.code_plot,
        position_waypoint: values.position_waypoint,
        control_point: id,
      };

      if (editingSample) {
        // Update existing sample
        await apiClient.patch(`/samples/${editingSample.id}/`, sampleData);
        message.success('Muestra actualizada correctamente');
      } else {
        // Create new sample
        await apiClient.post('/samples/', sampleData);
        message.success('Muestra creada correctamente');
      }

      setSampleModalVisible(false);
      sampleForm.resetFields();
      refetch();
    } catch (error: any) {
      message.error(editingSample ? 'Error al actualizar la muestra' : 'Error al crear la muestra');
      console.error('Error saving sample:', error);
    } finally {
      setIsSavingSample(false);
    }
  };

  // Get status color
  const getStatusColor = (status: ControlPointStatus): string => {
    return status === "open" ? "green" : "red";
  };

  // Get status text
  const getStatusText = (status: ControlPointStatus): string => {
    return status === "open"
      ? t("controlPoint.status.open", "Abierto")
      : t("controlPoint.status.closed", "Cerrado");
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!controlPoint) {
    return (
      <Card>
        <Empty description={t("controlPoint.notFound", "Punto de control no encontrado")} />
      </Card>
    );
  }

  // Reviews table columns
  const reviewColumns = [
    {
      title: "Auditoría",
      dataIndex: "audit_reference",
      key: "audit_reference",
      width: 150,
    },
    {
      title: "Fecha Detección",
      dataIndex: "audit_date",
      key: "audit_date",
      width: 150,
      render: (date: string) => date ? dayjs(date).format("DD/MM/YYYY") : "-",
    },
    {
      title: "Auditor",
      dataIndex: "auditor_name",
      key: "auditor_name",
      width: 200,
    },
    {
      title: "Superficie (ha)",
      dataIndex: "estimated_surface",
      key: "estimated_surface",
      width: 130,
      align: "center" as const,
      render: (value: number) => value ? value.toFixed(2) : "-",
    },
    {
      title: "Comentario",
      dataIndex: "comment",
      key: "comment",
      render: (comment: string) => comment || "-",
    },
  ];

  // Samples table columns
  const sampleColumns = [
    {
      title: "Código Laboratorio",
      dataIndex: "code_sample",
      key: "code_sample",
      width: 150,
    },
    {
      title: "Código Parcela",
      dataIndex: "code_plot",
      key: "code_plot",
      width: 150,
    },
    {
      title: "Waypoint",
      dataIndex: "position_waypoint",
      key: "position_waypoint",
      width: 120,
    },
    {
      title: "Estado",
      dataIndex: "state",
      key: "state",
      width: 150,
      render: (state: string) => state || "-",
    },
    {
      title: "Auditoría",
      dataIndex: "audit_reference",
      key: "audit_reference",
      width: 150,
    },
    {
      title: "Acciones",
      key: "actions",
      width: 120,
      fixed: "right" as const,
      render: (_: any, record: any) => (
        <Space>
          <Button
            type="link"
            size="small"
            onClick={() => handleEditSample(record)}
          >
            Editar
          </Button>
          <Button
            type="link"
            danger
            size="small"
            onClick={() => handleDeleteSample(record.id)}
          >
            Eliminar
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="control-point-show">
      <Show
        isLoading={isLoading}
        headerButtons={() => (
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
            >
              {t("common.back", "Volver")}
            </Button>
            {controlPoint?.status === "open" && (
              <Button
                danger
                icon={<CloseCircleOutlined />}
                onClick={handleOpenCloseModal}
              >
                Cerrar Punto de Control
              </Button>
            )}
          </Space>
        )}
        title={controlPoint.name}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {/* General Information */}
          <Card
            title={
              <Space>
                <InfoCircleOutlined />
                {t("controlPoint.generalInfo", "Información General")}
              </Space>
            }
          >
            <Descriptions bordered column={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 2 }}>
              <Descriptions.Item label="Productor">
                {controlPoint.producer_name}
              </Descriptions.Item>

              <Descriptions.Item label="Finca">
                {controlPoint.farm_name}
              </Descriptions.Item>

              <Descriptions.Item label="Motivo">
                {controlPoint.reason_name}
              </Descriptions.Item>

              <Descriptions.Item label="Fecha Detección">
                {controlPoint.detection_date
                  ? dayjs(controlPoint.detection_date).format("DD/MM/YYYY")
                  : "-"}
              </Descriptions.Item>

              <Descriptions.Item label="Estado">
                <Tag color={getStatusColor(controlPoint.status)}>
                  {getStatusText(controlPoint.status)}
                </Tag>
              </Descriptions.Item>

              <Descriptions.Item label="Superficie Estimada">
                {controlPoint.total_estimated_surface?.toFixed(2)} ha
              </Descriptions.Item>

              <Descriptions.Item label="Nº Revisiones">
                {controlPoint.review_count}
              </Descriptions.Item>

              <Descriptions.Item label="Nº Muestras">
                {controlPoint.sample_count}
              </Descriptions.Item>

              <Descriptions.Item label="Fotos">
                <Button
                  type={controlPoint.photo_count > 0 ? "link" : "default"}
                  size="small"
                  icon={<PictureOutlined />}
                  onClick={handleViewPhotos}
                  disabled={controlPoint.photo_count === 0}
                >
                  {controlPoint.photo_count}
                </Button>
              </Descriptions.Item>
            </Descriptions>

            {/* Closure Information - Only shown when closed */}
            {controlPoint.status === "closed" && (
              <>
                <div style={{ marginTop: 24, marginBottom: 8, fontWeight: 600, fontSize: 16 }}>
                  Información de Cierre
                </div>
                <Descriptions bordered column={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 2 }}>
                  <Descriptions.Item label="Fecha de Cierre">
                    {controlPoint.closed_date
                      ? dayjs(controlPoint.closed_date).format("DD/MM/YYYY")
                      : "-"}
                  </Descriptions.Item>

                  {controlPoint.authorized_farm_reference && (
                    <Descriptions.Item label="Referencia Finca Autorizada">
                      {controlPoint.authorized_farm_reference}
                    </Descriptions.Item>
                  )}

                  {controlPoint.converted_hectares !== undefined && controlPoint.converted_hectares > 0 && (
                    <Descriptions.Item label="Hectáreas Convertidas">
                      {controlPoint.converted_hectares.toFixed(2)} ha
                    </Descriptions.Item>
                  )}

                  {controlPoint.comment_closed && (
                    <Descriptions.Item label="Comentario de Cierre" span={2}>
                      {controlPoint.comment_closed}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </>
            )}
          </Card>

          {/* Review History Table */}
          <Card
            title={
              <Space>
                <HistoryOutlined />
                {t("controlPoint.reviewHistory", "Histórico de Revisiones")}
              </Space>
            }
          >
            {controlPoint.reviews && controlPoint.reviews.length > 0 ? (
              <Table
                dataSource={controlPoint.reviews.sort((a, b) => 
                  new Date(b.audit_date).getTime() - new Date(a.audit_date).getTime()
                )}
                columns={reviewColumns}
                rowKey="id"
                pagination={false}
              />
            ) : (
              <Empty description={t("controlPoint.noReviews", "No hay revisiones registradas")} />
            )}
          </Card>

          {/* Samples Table */}
          <Card
            title={
              <Space>
                <ExperimentOutlined />
                {t("controlPoint.samples", "Muestras")}
              </Space>
            }
            extra={
              <Button type="primary" onClick={handleCreateSample}>
                Crear Muestra
              </Button>
            }
          >
            <Table
              dataSource={controlPoint.all_samples || []}
              columns={sampleColumns}
              rowKey="id"
              pagination={false}
              locale={{ emptyText: "No hay muestras registradas" }}
            />
          </Card>

          {/* Location Map */}
          {controlPoint.farm && (
            <Card
              title={
                <Space>
                  <EnvironmentOutlined />
                  Ubicación y Parcelas
                </Space>
              }
            >
              <ControlPointLocationMap
                controlPointId={controlPoint.id}
                farmId={controlPoint.farm}
                geometry={controlPoint.geom || undefined}
              />
            </Card>
          )}
        </Space>
      </Show>

      {/* Photo Gallery Modal */}
      {controlPoint && (
        <PhotoGalleryModal
          visible={photoGalleryVisible}
          onClose={() => setPhotoGalleryVisible(false)}
          entityId={controlPoint.id}
          entityName={controlPoint.name}
          entityType="controlPoint"
        />
      )}

      {/* Close Control Point Modal */}
      <Modal
        title="Cerrar Punto de Control"
        open={closeModalVisible}
        onCancel={handleCloseModalCancel}
        onOk={() => form.submit()}
        confirmLoading={isClosing}
        width={600}
        okText="Cerrar Punto de Control"
        cancelText="Cancelar"
        okButtonProps={{ danger: true }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCloseControlPoint}
        >
          <Form.Item
            label="Fecha de Cierre"
            name="closed_date"
            rules={[{ required: true, message: "La fecha de cierre es requerida" }]}
          >
            <DatePicker
              style={{ width: "100%" }}
              format="DD/MM/YYYY"
              placeholder="Seleccione la fecha de cierre"
            />
          </Form.Item>

          <Form.Item
            label="Comentario de Cierre"
            name="comment_closed"
            rules={[{ required: true, message: "El comentario es requerido" }]}
          >
            <TextArea
              rows={4}
              placeholder="Describa el motivo del cierre del punto de control"
            />
          </Form.Item>

          <Form.Item
            label="Referencia Finca Autorizada"
            name="authorized_farm_reference"
            tooltip="Escriba al menos 2 caracteres para buscar por nombre de finca o productor"
          >
            <AutoComplete
              options={farmOptions}
              onSearch={handleSearchFarms}
              onClear={() => setFarmOptions([])}
              placeholder="Busque la finca autorizada (mín. 2 caracteres)"
              notFoundContent={searchingFarms ? <Spin size="small" /> : "No se encontraron fincas"}
              allowClear
            />
          </Form.Item>

          <Form.Item
            label="Hectáreas Convertidas"
            name="converted_hectares"
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              step={0.01}
              precision={2}
              placeholder="0.00"
              addonAfter="ha"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Sample Create/Edit Modal */}
      <Modal
        title={editingSample ? "Editar Muestra" : "Crear Muestra"}
        open={sampleModalVisible}
        onOk={() => sampleForm.submit()}
        onCancel={() => {
          setSampleModalVisible(false);
          sampleForm.resetFields();
          setEditingSample(null);
        }}
        confirmLoading={isSavingSample}
        okText={editingSample ? "Actualizar" : "Crear"}
        cancelText="Cancelar"
      >
        <Form
          form={sampleForm}
          layout="vertical"
          onFinish={handleSaveSample}
        >
          <Form.Item
            label="Código Laboratorio"
            name="code_sample"
            rules={[{ required: true, message: 'El código de laboratorio es obligatorio' }]}
          >
            <Input placeholder="Ingrese el código de laboratorio" />
          </Form.Item>

          <Form.Item
            label="Código Parcela"
            name="code_plot"
            rules={[{ required: true, message: 'El código de parcela es obligatorio' }]}
          >
            <Input placeholder="Ingrese el código de parcela" />
          </Form.Item>

          <Form.Item
            label="Número Waypoint"
            name="position_waypoint"
            rules={[{ required: true, message: 'El número de waypoint es obligatorio' }]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={1}
              placeholder="Ingrese el número de waypoint"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
